import { useCallback, useMemo, useState } from "react";

/**
 * useSelection — pattern used by every admin queue tab.
 *
 * Returns:
 *   selected           — Set<string>
 *   isSelected(id)
 *   toggle(id)
 *   selectAll(ids)     — union of the given ids
 *   clear()
 *   count
 *   allSelected(ids)   — helper for the header checkbox tri-state
 */
export const useSelection = () => {
  const [selected, setSelected] = useState(() => new Set());

  const isSelected = useCallback((id) => selected.has(id), [selected]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((ids) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const allSelected = useCallback(
    (ids) => ids.length > 0 && ids.every((id) => selected.has(id)),
    [selected],
  );

  const count = useMemo(() => selected.size, [selected]);
  const ids = useMemo(() => Array.from(selected), [selected]);

  return { selected, ids, isSelected, toggle, selectAll, clear, count, allSelected };
};
